import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, useAuth } from '../App';
import { useT } from '../i18n';
import DomainCheckHints, { domainTxtHint } from './DomainCheckHints';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Link2, Plus, RefreshCw, Star, Trash2, Copy, CheckCircle2, AlertCircle, Clock, Layers, Shuffle, Heart, HeartCrack, Search, SearchX, Pencil } from 'lucide-react';
import XpnsrPageHeader from './XpnsrPageHeader';
import ConfirmDialog from './ConfirmDialog';
import XpnsrEmpty from './XpnsrEmpty';
import DomainPoolEditDialog from './DomainPoolEditDialog';

const StatusBadge = ({ status, t }) => {
  if (status === 'verified') {
    return (
      <Badge className="bg-primary/15 text-primary border border-primary/30">
        <CheckCircle2 className="w-3 h-3 mr-1" /> {t('Verified')}
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge variant="destructive">
        <AlertCircle className="w-3 h-3 mr-1" /> {t('Failed')}
      </Badge>
    );
  }
  return (
    <Badge variant="outline">
      <Clock className="w-3 h-3 mr-1" /> {t('Pending')}
    </Badge>
  );
};

const SEO_ROOT_LABELS = {
  index_stable: 'Root: stable index',
  crawler_split: 'Root: SEO split',
  funnel: 'Root: funnel',
  none: 'Root: 404',
};

export default function Domains() {
  const { t } = useT();
  const { user } = useAuth();
  const [domains, setDomains] = useState([]);
  const [hostname, setHostname] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [pools, setPools] = useState([]);
  const [poolName, setPoolName] = useState('');
  const [poolStrategy, setPoolStrategy] = useState('round_robin');
  const [poolDomainIds, setPoolDomainIds] = useState([]);
  const [edgeIp, setEdgeIp] = useState('');
  const [landings, setLandings] = useState([]);
  const [seoStatus, setSeoStatus] = useState({});
  const [adminEdgeIp, setAdminEdgeIp] = useState('');
  const [edgeSaving, setEdgeSaving] = useState(false);
  const [setupCmd, setSetupCmd] = useState('');

  const load = async () => {
    try {
      const [domainsRes, poolsRes, edgeRes, landingsRes, seoRes] = await Promise.allSettled([
        axios.get(`${API}/domains`),
        axios.get(`${API}/domain-pools`),
        axios.get(`${API}/domains/edge-info`),
        axios.get(`${API}/landing-pages`),
        axios.get(`${API}/domains/seo-status`),
      ]);
      if (domainsRes.status === 'fulfilled') setDomains(domainsRes.value.data || []);
      if (poolsRes.status === 'fulfilled') setPools(poolsRes.value.data || []);
      else setPools([]);
      if (edgeRes.status === 'fulfilled') setEdgeIp(edgeRes.value.data?.edge_ip || '');
      if (landingsRes.status === 'fulfilled') setLandings((landingsRes.value.data || []).filter((l) => l.is_uploaded && l.slug));
      if (seoRes.status === 'fulfilled') {
        const m = {};
        (seoRes.value.data || []).forEach((s) => { m[s.id] = s; });
        setSeoStatus(m);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!user || !user.is_admin) return;
    axios
      .get(API + '/admin/edge-config')
      .then((r) => {
        setAdminEdgeIp(r.data && r.data.edge_ip ? r.data.edge_ip : '');
        setSetupCmd(r.data && r.data.setup_command ? r.data.setup_command : '');
      })
      .catch(() => {});
  }, [user]);

  const saveEdgeIp = async () => {
    setEdgeSaving(true);
    try {
      const r = await axios.put(API + '/admin/edge-config', { edge_ip: adminEdgeIp.trim() });
      setEdgeIp((r.data && r.data.edge_ip) || '');
      toast.success(t('Edge server updated'));
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.detail) || t('Failed to update'));
    } finally {
      setEdgeSaving(false);
    }
  };

  const copySetup = () => {
    try {
      navigator.clipboard.writeText(setupCmd || '');
      toast.success(t('Copied'));
    } catch (e) {
      toast.error(t('Failed to copy'));
    }
  };

  const add = async () => {
    if (!hostname.trim()) return;
    try {
      await axios.post(`${API}/domains`, { hostname: hostname.trim() });
      setHostname('');
      toast.success(t('Domain added'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Failed to add domain'));
    }
  };

  const verify = async (id) => {
    setBusy(id);
    try {
      const r = await axios.post(`${API}/domains/${id}/verify`);
      if (r.data.verified && r.data.routing_ok === false) {
        toast.warning(t('Ownership confirmed, but the domain does not point to the tracker yet.'));
      } else if (r.data.verified) {
        toast.success(r.data.routing_ok ? t('Ownership confirmed and the domain points to the tracker.') : t('Domain verified'));
      } else {
        const host = (domains.find((x) => x.id === id) || {}).hostname;
        toast.error(domainTxtHint(t, r.data.last_error_code, host) || r.data.error || t('Verification failed'));
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Verification failed'));
    } finally {
      setBusy(null);
    }
  };

  const setDefault = async (id) => {
    try {
      await axios.post(`${API}/domains/${id}/set-default`);
      toast.success(t('Default domain updated'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePoolTarget, setDeletePoolTarget] = useState(null);
  const [editPool, setEditPool] = useState(null);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/domains/${id}`);
      setDeleteTarget(null);
      toast.success(t('Domain deleted'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    }
  };

  const toggleHealth = async (d) => {
    const next = (d.health_status === 'unhealthy') ? 'healthy' : 'unhealthy';
    try {
      await axios.post(`${API}/domains/${d.id}/health`, { health_status: next });
      toast.success(next === 'healthy' ? t('Domain marked healthy') : t('Domain pulled from rotation'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    }
  };

  const toggleIndexing = async (d) => {
    const next = !(d.allow_indexing !== false); // indexable now -> close, else open
    try {
      await axios.post(`${API}/domains/${d.id}/indexing`, { allow_indexing: next });
      toast.success(next ? t('Domain opened for indexing') : t('Domain closed from indexing'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    }
  };

  const setIndexPage = async (d, landingId) => {
    try {
      await axios.post(`${API}/domains/${d.id}/index-page`, { index_landing_id: landingId || null });
      toast.success(landingId ? t('Index page set') : t('Index page cleared'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    }
  };

  const toggleSeoSplit = async (d) => {
    const next = !d.seo_crawler_split;
    try {
      await axios.post(`${API}/domains/${d.id}/seo-crawler-split`, { seo_crawler_split: next });
      toast.success(
        next
          ? t('SEO crawler split on: verified crawlers index the page, humans go to the funnel')
          : t('SEO crawler split off'),
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    }
  };

  const toggleAutogen = async (d) => {
    const next = !d.seo_autogen_files;
    try {
      await axios.post(`${API}/domains/${d.id}/seo-autogen`, { seo_autogen_files: next });
      toast.success(
        next
          ? t('Fallback robots.txt/sitemap.xml on (only when your bundle has none)')
          : t('Fallback robots.txt/sitemap.xml off'),
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    }
  };

  const seoBadgeRow = (d) => {
    const s = seoStatus[d.id];
    if (!s || d.status !== 'verified') return null;
    const pill = (text, tone) => (
      <span className={`text-[0.7rem] px-2 py-0.5 rounded-md border ${tone}`}>{text}</span>
    );
    const rootTone =
      s.root_behavior === 'funnel'
        ? 'border-amber-500/40 text-amber-500'
        : s.root_behavior === 'none'
          ? 'border-border text-muted-foreground'
          : 'border-primary/40 text-primary';
    const srcLabel = (v) => (v === 'bundle' ? t('own') : v === 'fallback' ? t('fallback') : t('none'));
    return (
      <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`seo-status-${d.id}`}>
        {pill(t(SEO_ROOT_LABELS[s.root_behavior] || s.root_behavior), rootTone)}
        {pill(`robots.txt: ${srcLabel(s.robots)}`, 'border-border text-muted-foreground')}
        {pill(`sitemap.xml: ${srcLabel(s.sitemap)}`, 'border-border text-muted-foreground')}
        {pill(
          s.allow_indexing ? t('indexable') : t('no-index'),
          s.allow_indexing ? 'border-border text-muted-foreground' : 'border-destructive/40 text-destructive',
        )}
        {s.bound_campaign && pill(`→ ${s.bound_campaign.name}`, 'border-border text-muted-foreground')}
      </div>
    );
  };

  const createPool = async () => {
    if (!poolName.trim() || poolDomainIds.length === 0) return;
    try {
      await axios.post(`${API}/domain-pools`, {
        name: poolName.trim(),
        strategy: poolStrategy,
        domain_ids: poolDomainIds,
      });
      setPoolName(''); setPoolDomainIds([]); setPoolStrategy('round_robin');
      toast.success(t('Domain pool created'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Failed to create pool'));
    }
  };

  const removePool = async (id) => {
    try {
      await axios.delete(`${API}/domain-pools/${id}`);
      setDeletePoolTarget(null);
      toast.success(t('Domain pool deleted'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    }
  };

  const copy = (s) => {
    navigator.clipboard.writeText(s);
    toast.success(t('Copied'));
  };

  return (
    <div className="px-8 pt-2 pb-10 max-w-5xl space-y-6">
      <XpnsrPageHeader
        kicker={t('Tracker domains')}
        title={t('Custom domains')}
        subtitle={t('Bind your own domain to mask tracking links. Point an A record to your tracking server and verify ownership via TXT record.')}
      />

      {user && user.is_admin && (
        <Card className="mb-6 border-amber-500/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="w-4 h-4" /> {t('Server migration (admin only)')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div>
              {t('Current server IP:')} <span className="font-mono font-semibold">{edgeIp || '—'}</span>
            </div>

            <div className="space-y-2">
              <div className="font-semibold">{t('Step 1. Provision a new server')}</div>
              <p className="text-xs text-muted-foreground">
                {t('Create an EC2 instance in AWS (Ubuntu, open ports 80 and 443), SSH into it and paste this entire command. It prints the new server IP at the end.')}
              </p>
              <div className="relative">
                <pre className="bg-surface-2 p-3 rounded text-xs overflow-auto max-h-56 whitespace-pre-wrap">{setupCmd || t('Loading…')}</pre>
                <Button size="sm" variant="outline" className="absolute top-2 right-2" onClick={copySetup}>
                  <Copy className="w-3 h-3 mr-1" /> {t('Copy')}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="font-semibold">{t('Step 2. Enter the new IP')}</div>
              <p className="text-xs text-muted-foreground">
                {t('Paste the IP from Step 1 and press Save. Every client immediately gets the new IP for their A record — no restart needed.')}
              </p>
              <div className="flex gap-2">
                <Input placeholder="1.2.3.4" value={adminEdgeIp} onChange={(e) => setAdminEdgeIp(e.target.value)} />
                <Button onClick={saveEdgeIp} disabled={edgeSaving}>
                  {edgeSaving ? t('Saving…') : t('Save')}
                </Button>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              {t('Tip: if you reassign the old Elastic IP to the new server in AWS, nothing needs to change here — nor for clients.')}
            </p>
          </CardContent>
        </Card>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Plus className="w-4 h-4" /> {t('Add domain')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="track.yourdomain.com"
              value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && add()}
            />
            <Button onClick={add}>{t('Add')}</Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            {t('After adding, configure DNS for your domain (TXT record for verification, CNAME or A record to route traffic), then press Verify.')}
          </p>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-muted-foreground">{t('Loading…')}</p>
      ) : domains.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <XpnsrEmpty
              icon={Link2}
              title={t('No domains yet')}
              subtitle={t('Add a custom domain to serve tracking links from your own branded URL.')}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {domains.map((d) => (
            <Card key={d.id}>
              <CardContent className="py-5">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-base font-semibold">{d.hostname}</span>
                      <StatusBadge status={d.status} t={t} />
                      {d.is_default && (
                        <Badge className="bg-primary/15 text-primary border border-primary/30">
                          <Star className="w-3 h-3 mr-1" /> {t('Default')}
                        </Badge>
                      )}
                    </div>
                    <DomainCheckHints d={d} t={t} />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => verify(d.id)} disabled={busy === d.id}>
                      <RefreshCw className={`w-4 h-4 mr-1 ${busy === d.id ? 'animate-spin' : ''}`} />
                      {t('Verify')}
                    </Button>
                    {d.status === 'verified' && !d.is_default && (
                      <Button variant="outline" size="sm" onClick={() => setDefault(d.id)}>
                        <Star className="w-4 h-4 mr-1" /> {t('Set default')}
                      </Button>
                    )}
                    {d.status === 'verified' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleHealth(d)}
                        title={d.health_status === 'unhealthy' ? t('Restore to rotation') : t('Pull out of rotation')}
                      >
                        {d.health_status === 'unhealthy'
                          ? <HeartCrack className="w-4 h-4 mr-1 text-destructive" />
                          : <Heart className="w-4 h-4 mr-1 text-primary" />}
                        {d.health_status === 'unhealthy' ? t('Banned') : t('Healthy')}
                      </Button>
                    )}
                    {d.status === 'verified' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleIndexing(d)}
                        title={d.allow_indexing !== false ? t('Open for search engines — click to close') : t('Closed from search engines — click to open')}
                      >
                        {d.allow_indexing !== false
                          ? <Search className="w-4 h-4 mr-1 text-primary" />
                          : <SearchX className="w-4 h-4 mr-1 text-destructive" />}
                        {d.allow_indexing !== false ? t('Indexable') : t('No-index')}
                      </Button>
                    )}
                    <Button variant="outline" size="sm" aria-label={t('Delete domain')} title={t('Delete domain')} onClick={() => setDeleteTarget(d)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {d.status === 'verified' && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{t('Index page (SEO, served at the root with 200):')}</span>
                    <select
                      value={d.index_landing_id || ''}
                      onChange={(e) => setIndexPage(d, e.target.value)}
                      className="text-xs bg-surface-2 border border-border rounded-md px-2 py-1 min-w-[220px] text-foreground"
                    >
                      <option value="">{t('— none (root opens the funnel) —')}</option>
                      {landings.map((l) => (
                        <option key={l.id} value={l.id}>{l.name}</option>
                      ))}
                    </select>
                    {d.index_landing_id && (
                      <label
                        className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap"
                        title={t('When on, DNS-verified search crawlers get the index page (for ranking) and humans are sent to the bound campaign funnel. Needs an index page + a campaign bound to this domain.')}
                      >
                        <input
                          type="checkbox"
                          className="xp-checkbox"
                          checked={!!d.seo_crawler_split}
                          onChange={() => toggleSeoSplit(d)}
                          data-testid={`seo-split-${d.id}`}
                        />
                        <span>{t('SEO split (crawlers → index, humans → funnel)')}</span>
                      </label>
                    )}
                    <label
                      className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap"
                      title={t('Serve a generated robots.txt / sitemap.xml at the domain root ONLY when your uploaded bundle has none. Your own SEO files always take priority.')}
                    >
                      <input
                        type="checkbox"
                        className="xp-checkbox"
                        checked={!!d.seo_autogen_files}
                        onChange={() => toggleAutogen(d)}
                        data-testid={`seo-autogen-${d.id}`}
                      />
                      <span>{t('Fallback robots/sitemap')}</span>
                    </label>
                  </div>
                )}

                {seoBadgeRow(d)}

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-md border border-border bg-surface-2 p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">
                        {t('TXT verification record')}
                      </span>
                      <button
                        onClick={() => copy(d.verification_token)}
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> {t('Copy value')}
                      </button>
                    </div>
                    <div className="font-mono text-xs break-all">
                      <div><span className="text-muted-foreground">{t('Name')}:</span> _xpnsr-verify.{d.hostname}</div>
                      <div><span className="text-muted-foreground">{t('Value')}:</span> {d.verification_token}</div>
                    </div>
                  </div>
                  <div className="rounded-md border border-border bg-surface-2 p-3">
                    <div className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground mb-1">
                      {d.cf_cname_target ? t('Routing record (CNAME)') : t('Routing record (A record)')}
                    </div>
                    <div className="font-mono text-xs break-all">
                      <div><span className="text-muted-foreground">{t('Name')}:</span> {d.hostname}</div>
                      <div><span className="text-muted-foreground">{t('Type')}:</span> {d.cf_cname_target ? 'CNAME' : 'A'}</div>
                      <div><span className="text-muted-foreground">{t('Value')}:</span> {d.cf_cname_target || edgeIp || t('IP of your tracking server')}</div>
                    </div>
                  </div>
                </div>


                {d.status === 'verified' && (
                  <div className="mt-3 text-xs text-muted-foreground">
                    {t('Tracking links will use')}{' '}
                    <span className="font-mono text-foreground">https://{d.hostname}/r/&lt;tracking_id&gt;</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="w-4 h-4" /> {t('Domain pools')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            {t('Bundle verified domains into a pool to give out links: one link per domain, for example one per ad account (campaign page, Links for ad accounts). Pull a banned domain out of rotation and it is no longer given out; links already placed keep working.')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Input
              placeholder={t('Pool name')}
              value={poolName}
              onChange={(e) => setPoolName(e.target.value)}
            />
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={poolStrategy}
              onChange={(e) => setPoolStrategy(e.target.value)}
            >
              <option value="round_robin">{t('Round robin')}</option>
              <option value="weighted">{t('Weighted')}</option>
              <option value="sticky">{t('Sticky (per-visitor)')}</option>
            </select>
            <Button onClick={createPool} disabled={!poolName.trim() || poolDomainIds.length === 0}>
              <Plus className="w-4 h-4 mr-1" /> {t('Create pool')}
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {domains.filter((d) => d.status === 'verified').map((d) => (
              <label key={d.id} className="flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-border">
                <input
                  type="checkbox"
                  checked={poolDomainIds.includes(d.id)}
                  onChange={(e) => {
                    setPoolDomainIds((prev) =>
                      e.target.checked ? [...prev, d.id] : prev.filter((x) => x !== d.id),
                    );
                  }}
                />
                <span className="font-mono">{d.hostname}</span>
              </label>
            ))}
          </div>

          {pools.length > 0 && (
            <div className="space-y-2">
              {pools.map((p) => (
                <div key={p.id} className="rounded-md border border-border p-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Shuffle className="w-3 h-3" /> {p.strategy}
                        {' · '}
                        {(p.domain_ids || []).length} {t('domains')}
                      </div>
                    </div>
                    <div className="flex gap-1">
                    <Button variant="outline" size="sm" aria-label={t('Edit item')} title={t('Edit item')} onClick={() => setEditPool(p)} data-testid={`pool-edit-${p.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" aria-label={t('Delete domain pool')} title={t('Delete domain pool')} onClick={() => setDeletePoolTarget(p)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t('Delete domain')}
        description={t('This will permanently delete "{name}". Campaigns using this domain will fall back to the default domain.').replace('{name}', deleteTarget?.hostname || '')}
        confirmLabel={t('Delete')}
        onConfirm={() => remove(deleteTarget?.id)}
      />
      <DomainPoolEditDialog
        pool={editPool}
        domains={domains}
        open={!!editPool}
        onOpenChange={(open) => { if (!open) setEditPool(null); }}
        onSaved={(upd) => upd && setPools((prev) => prev.map((x) => (x.id === upd.id ? upd : x)))}
      />
      <ConfirmDialog
        open={!!deletePoolTarget}
        onOpenChange={(open) => { if (!open) setDeletePoolTarget(null); }}
        title={t('Delete domain pool')}
        description={t('This will permanently delete pool "{name}".').replace('{name}', deletePoolTarget?.name || '')}
        confirmLabel={t('Delete')}
        onConfirm={() => removePool(deletePoolTarget?.id)}
      />
    </div>
  );
}
